/**
 * usePageVisibility — React hook wrapping the Page Visibility API.
 *
 * Returns { isHidden, isVisible } which reflect document.hidden.
 * Used by useSession to drive pause/resume on tab backgrounding (Pitfall H / T-02-VOICE-02).
 *
 * The hook subscribes to 'visibilitychange' events and cleans up on unmount.
 */

import { useEffect, useState } from 'react';

export interface UsePageVisibilityReturn {
  /** True when the document is hidden (tab backgrounded, window minimized, etc.) */
  isHidden: boolean;
  /** Convenience inverse of isHidden */
  isVisible: boolean;
}

export function usePageVisibility(): UsePageVisibilityReturn {
  // Initialize from current document state (handles SSR-safe access)
  const [isHidden, setIsHidden] = useState<boolean>(
    typeof document !== 'undefined' ? document.hidden : false
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsHidden(document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return { isHidden, isVisible: !isHidden };
}
